/**
 * Admin · Emails: everything sent and answered lately (AdminActivity.tsx),
 * edit the built-in lifecycle templates, send a template or custom copy (dry
 * run first, then a confirmed real send). The survey answers the emails
 * collect are also in the Feedback tab (AdminFeedback.tsx). See EMAIL.md for
 * the backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CustomEmailCopy,
  EMAIL_LANGS,
  EmailDryRunResult,
  EmailLang,
  EmailSegmentType,
  EmailSendJob,
  EmailSendRequest,
  EmailTemplateDetail,
  EmailTemplateList,
  RenderedEmail,
  TemplateCopy,
  emailAdminApi,
  AdminApiError,
} from './adminApi';
import AdminActivity from './AdminActivity';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

type Section = 'activity' | 'templates' | 'compose';

const LANG_LABELS: Record<EmailLang, string> = { en: 'English', ja: '日本語' };
const SEGMENT_LABELS: Record<EmailSegmentType, string> = {
  recent_purchasers: 'Purchased in the last N days',
  recent_signups: 'Signed up in the last N days',
};
const EMPTY_COPY: TemplateCopy = { subject: '', preheader: '', html: '', text: '', cta: '' };
/** Pause between batches while a send is open; each batch request itself
 * takes up to ~15 s. Errors back off from here up to SEND_JOB_MAX_BACKOFF_MS. */
export const SEND_JOB_POLL_MS = 1000;
const SEND_JOB_MAX_BACKOFF_MS = 30000;

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** Answer sentiment, matching the colored buttons in the emails. */
const ANSWER_TONE: Record<string, string> = {
  yes: 'positive',
  very_well: 'positive',
  partly: 'partial',
  somewhat: 'partial',
  no: 'negative',
  not_well: 'negative',
  not_yet: 'neutral',
  too_early: 'neutral',
};

export function AnswerBadge({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="admin-muted">—</span>;
  return <span className={`answer-badge ${ANSWER_TONE[value] || ''}`}>{value}</span>;
}

/** Status of a recipient in a dry run or send, of an emailLog entry, or of a send job. */
export function EmailStatusBadge({ status }: { status: string | null | undefined }) {
  const tone =
    status === 'sent' || status === 'would_send' || status === 'completed'
      ? 'positive'
      : status === 'failed' || status === 'not_configured'
        ? 'negative'
        : status === 'suppressed' || status === 'interrupted' || status === 'cancelled'
          ? 'partial'
          : '';
  return <span className={`answer-badge ${tone}`}>{status || 'unknown'}</span>;
}

/** Rendered email in a sandboxed frame (no scripts, links not followable, so
 * preview clicks never record a survey answer). */
function EmailPreview({ email, caption }: { email: RenderedEmail | null; caption?: string }) {
  const [view, setView] = useState<'html' | 'text'>('html');
  if (!email) return <div className="admin-muted">No preview yet.</div>;
  return (
    <div className="email-preview">
      <div className="email-preview-head">
        <div>
          <div className="admin-muted" style={{ fontSize: 12 }}>
            {caption || 'Subject'}
          </div>
          <strong>{email.subject}</strong>
        </div>
        <div className="admin-tabs">
          <button
            className={`admin-inline-btn ${view === 'html' ? 'active' : ''}`}
            onClick={() => setView('html')}
          >
            HTML
          </button>
          <button
            className={`admin-inline-btn ${view === 'text' ? 'active' : ''}`}
            onClick={() => setView('text')}
          >
            Text
          </button>
        </div>
      </div>
      {view === 'html' ? (
        <iframe
          className="email-preview-frame"
          title="Email preview"
          sandbox=""
          srcDoc={email.html}
        />
      ) : (
        <pre className="admin-prompt email-preview-text">{email.text}</pre>
      )}
    </div>
  );
}

function LangTabs({
  lang,
  onChange,
  marks,
}: {
  lang: EmailLang;
  onChange: (lang: EmailLang) => void;
  marks?: Partial<Record<EmailLang, string>>;
}) {
  return (
    <div className="admin-tabs">
      {EMAIL_LANGS.map((l) => (
        <button
          key={l}
          className={`admin-inline-btn ${lang === l ? 'active' : ''}`}
          onClick={() => onChange(l)}
        >
          {LANG_LABELS[l]}
          {marks?.[l] ? ` ${marks[l]}` : ''}
        </button>
      ))}
    </div>
  );
}

/** Subject / preheader / CTA / HTML / text fields for one language. */
function CopyFields({
  copy,
  onChange,
  defaults,
  textHint,
}: {
  copy: TemplateCopy;
  onChange: (copy: TemplateCopy) => void;
  defaults?: TemplateCopy;
  textHint: string;
}) {
  const field = (key: keyof TemplateCopy, label: string, hint: string, multiline = false) => {
    const edited = defaults && copy[key] !== defaults[key];
    return (
      <label className="email-field">
        <span className="email-field-label">
          {label}
          {edited && (
            <>
              <span className="answer-badge partial">edited</span>
              <button
                type="button"
                className="admin-back"
                onClick={() => onChange({ ...copy, [key]: defaults![key] })}
              >
                use default
              </button>
            </>
          )}
        </span>
        {multiline ? (
          <textarea
            className="admin-search email-code"
            rows={key === 'html' ? 16 : 10}
            value={copy[key]}
            onChange={(e) => onChange({ ...copy, [key]: e.target.value })}
            spellCheck={false}
          />
        ) : (
          <input
            className="admin-search"
            value={copy[key]}
            onChange={(e) => onChange({ ...copy, [key]: e.target.value })}
          />
        )}
        {hint && <span className="email-field-hint">{hint}</span>}
      </label>
    );
  };
  return (
    <div className="email-fields">
      {field('subject', 'Subject', '')}
      {field('preheader', 'Preheader', 'Inbox preview text shown after the subject.')}
      {field('cta', 'Button label', 'Placed at {{cta}}; links to the app. Empty = no button.')}
      {field(
        'html',
        'HTML body',
        '{{survey}} places the one-click question (appended at the end if missing).',
        true
      )}
      {field('text', 'Plain-text body', textHint, true)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

function TemplateList({
  list,
  onOpen,
}: {
  list: EmailTemplateList;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Template</th>
            <th>Sent when</th>
            <th>Survey</th>
            <th>Subject (en / ja)</th>
            <th>Copy</th>
          </tr>
        </thead>
        <tbody>
          {list.templates.map((t) => (
            <tr key={t.id} onClick={() => onOpen(t.id)}>
              <td>
                <strong>{t.id}</strong>
              </td>
              <td className="wrap">{t.description}</td>
              <td>{t.survey ?? 'none'}</td>
              <td className="wrap">
                {t.subject.en}
                <br />
                <span className="admin-muted">{t.subject.ja}</span>
              </td>
              <td>
                {t.customized ? (
                  <>
                    <span className="answer-badge partial">edited</span>
                    <div className="admin-muted" style={{ fontSize: 12 }}>
                      {fmtAdminDateTime(t.updatedAt)}
                      {t.updatedBy ? ` · ${t.updatedBy}` : ''}
                    </div>
                  </>
                ) : (
                  <span className="admin-muted">default</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TemplateEditor({ templateId, onBack }: { templateId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<EmailTemplateDetail | null>(null);
  const [drafts, setDrafts] = useState<Record<EmailLang, TemplateCopy> | null>(null);
  const [lang, setLang] = useState<EmailLang>('en');
  const [previewName, setPreviewName] = useState('Sam Sample');
  const [preview, setPreview] = useState<RenderedEmail | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const previewSeq = useRef(0);

  const apply = (d: EmailTemplateDetail) => {
    setDetail(d);
    setDrafts(d.effective);
  };

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setDrafts(null);
    emailAdminApi
      .template(templateId)
      .then((d) => !cancelled && apply(d))
      .catch((e) => !cancelled && setError(errorMessage(e, 'Failed to load template')));
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  // Live preview of the draft, debounced while typing.
  const draft = drafts?.[lang];
  useEffect(() => {
    if (!draft) return;
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      try {
        const rendered = await emailAdminApi.previewTemplate(templateId, {
          lang,
          draft,
          recipient: { name: previewName.trim() || undefined },
        });
        if (seq === previewSeq.current) {
          setPreview(rendered);
          setPreviewError('');
        }
      } catch (e) {
        if (seq === previewSeq.current) setPreviewError(errorMessage(e, 'Preview failed'));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [templateId, lang, draft, previewName]);

  if (!detail || !drafts) {
    return (
      <div>
        <button className="admin-back" onClick={onBack}>
          ← All templates
        </button>
        {error ? <div className="admin-error">{error}</div> : <div className="admin-muted">Loading…</div>}
      </div>
    );
  }

  const dirtyLangs = EMAIL_LANGS.filter(
    (l) => JSON.stringify(drafts[l]) !== JSON.stringify(detail.effective[l])
  );
  const dirty = dirtyLangs.length > 0;
  const editedLangs = EMAIL_LANGS.filter(
    (l) => JSON.stringify(drafts[l]) !== JSON.stringify(detail.default[l])
  );
  // The HTML changed but a hand-written text part didn't: text-only mail
  // clients would still show the old copy.
  const draftCopy = drafts[lang];
  const textMayBeStale =
    draftCopy.html !== detail.effective[lang].html &&
    draftCopy.text.trim() !== '' &&
    draftCopy.text === detail.effective[lang].text;

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      apply(await emailAdminApi.saveTemplate(templateId, drafts));
      setNotice('Saved. Automatic and admin sends of this template use the new copy from now on.');
    } catch (e) {
      setError(errorMessage(e, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${templateId} (both languages) to the built-in copy?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      apply(await emailAdminApi.resetTemplate(templateId));
      setNotice('Reset to the built-in copy.');
    } catch (e) {
      setError(errorMessage(e, 'Reset failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        className="admin-back"
        onClick={() => {
          if (!dirty || window.confirm('Discard unsaved changes?')) onBack();
        }}
      >
        ← All templates
      </button>
      <div className="admin-header" style={{ marginTop: 6 }}>
        <div>
          <h1 style={{ fontSize: 18 }}>{detail.id}</h1>
          <div className="admin-muted" style={{ fontSize: 13, marginTop: 4 }}>
            {detail.description} · survey: {detail.survey ?? 'none'}
            {detail.customized
              ? ` · edited ${fmtAdminDateTime(detail.updatedAt)}${detail.updatedBy ? ` by ${detail.updatedBy}` : ''}`
              : ' · built-in copy'}
          </div>
        </div>
        <div className="admin-tabs">
          <button className="admin-inline-btn" disabled={!dirty || busy} onClick={() => apply(detail)}>
            Discard changes
          </button>
          <button
            className="admin-inline-btn"
            disabled={busy || (!detail.customized && !dirty)}
            onClick={reset}
          >
            Reset to default
          </button>
          <button className="admin-primary-btn" disabled={!dirty || busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-notice">{notice}</div>}

      <div className="email-editor-grid">
        <div>
          <LangTabs
            lang={lang}
            onChange={setLang}
            marks={Object.fromEntries(
              EMAIL_LANGS.map((l) => [l, dirtyLangs.includes(l) ? '•' : editedLangs.includes(l) ? '✎' : ''])
            )}
          />
          <div className="email-field-hint" style={{ margin: '8px 0 12px' }}>
            Variables: {detail.variables.map((v) => `{{${v}}}`).join(' ')} · fallbacks:{' '}
            {'{{first_name|there}}'} · ✎ differs from the built-in copy, • unsaved
          </div>
          {textMayBeStale && (
            <div className="admin-notice">
              You changed the HTML body, but the plain-text part still has the old copy.{' '}
              <button
                type="button"
                className="admin-back"
                onClick={() => setDrafts({ ...drafts, [lang]: { ...draftCopy, text: '' } })}
              >
                Generate the text from the HTML instead
              </button>
            </div>
          )}
          <CopyFields
            copy={drafts[lang]}
            defaults={detail.default[lang]}
            onChange={(copy) => setDrafts({ ...drafts, [lang]: copy })}
            textHint="Leave empty to generate it from the HTML. {{survey}} becomes the answer links."
          />
        </div>
        <div>
          <label className="email-field" style={{ marginBottom: 12 }}>
            <span className="email-field-label">Preview as (name)</span>
            <input
              className="admin-search"
              value={previewName}
              onChange={(e) => setPreviewName(e.target.value)}
            />
          </label>
          {previewError && <div className="admin-error">{previewError}</div>}
          <EmailPreview email={preview} caption={`Subject (${LANG_LABELS[lang]}, unsaved draft)`} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compose & send
// ---------------------------------------------------------------------------

/** Emails from pasted text: one per line or separated by commas/semicolons/spaces. */
export function parseEmailList(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of input.split(/[\s,;<>]+/)) {
    const email = token.trim().replace(/^mailto:/i, '');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) continue;
    if (seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    out.push(email);
  }
  return out;
}

function parseVariables(input: string): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const line of input.split('\n')) {
    const i = line.indexOf('=');
    if (i <= 0) continue;
    const key = line.slice(0, i).trim();
    if (key) vars[key] = line.slice(i + 1).trim();
  }
  return vars;
}

function Compose({
  list,
  initialTo,
  initialJob,
}: {
  list: EmailTemplateList;
  initialTo: string;
  /** A send to show on open (from the activity feed). */
  initialJob: string;
}) {
  const [source, setSource] = useState<'template' | 'custom'>('template');
  const [templateId, setTemplateId] = useState(list.templates[0]?.id || '');
  const [campaign, setCampaign] = useState('');
  const [survey, setSurvey] = useState('');
  const [content, setContent] = useState<Record<EmailLang, TemplateCopy>>({
    en: EMPTY_COPY,
    ja: EMPTY_COPY,
  });
  const [lang, setLang] = useState<EmailLang>('en');
  const [variablesText, setVariablesText] = useState('');
  const [audience, setAudience] = useState<'list' | 'segment'>('list');
  const [emailsText, setEmailsText] = useState(initialTo);
  const [segment, setSegment] = useState<EmailSegmentType>('recent_purchasers');
  const [days, setDays] = useState(30);

  const [dryRun, setDryRun] = useState<{ key: string; result: EmailDryRunResult } | null>(null);
  const [job, setJob] = useState<EmailSendJob | null>(null);
  const [pollError, setPollError] = useState('');
  // Failed checks in a row (for backoff), and whether checking stopped for
  // good (a 4xx: the job is gone, or access was lost).
  const [pollFailures, setPollFailures] = useState(0);
  const [pollStopped, setPollStopped] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<'' | 'dry' | 'send' | 'cancel'>('');
  const [error, setError] = useState('');

  const openJob = useCallback(async (jobId: string) => {
    try {
      setJob(await emailAdminApi.sendJob(jobId));
      setPollError('');
      setPollFailures(0);
      setPollStopped(false);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load the send'));
    }
  }, []);

  // Show the send asked for, else reopen one still running (e.g. started
  // before a reload) so its progress shows. Past sends are in Recent activity.
  useEffect(() => {
    if (initialJob) {
      openJob(initialJob);
      return;
    }
    emailAdminApi
      .sendJobs()
      .then(({ jobs }) => {
        const running = jobs.find((j) => j.status === 'running');
        if (running) openJob(running.jobId);
      })
      .catch(() => {
        // A convenience; sending works without it.
      });
  }, [initialJob, openJob]);

  // While a running send is open, keep asking the backend to send its next
  // batch. (The scheduler tick does the same when nobody has it open.)
  const jobId = job?.jobId;
  const jobRunning = job?.status === 'running';
  useEffect(() => {
    if (!jobId || !jobRunning || pollStopped) return;
    let cancelled = false;
    const delay = Math.min(SEND_JOB_POLL_MS * 2 ** pollFailures, SEND_JOB_MAX_BACKOFF_MS);
    const timer = setTimeout(async () => {
      try {
        const next = await emailAdminApi.advanceSendJob(jobId);
        if (cancelled) return;
        setPollError('');
        setPollFailures(0);
        setJob((current) => (current?.jobId === jobId ? withPreview(next, current) : current));
      } catch (e) {
        if (cancelled) return;
        const status = e instanceof AdminApiError ? e.status : 0;
        setPollError(errorMessage(e, 'Could not check progress'));
        // A 4xx won't fix itself (job gone, signed out, no access): stop.
        if (status >= 400 && status < 500) setPollStopped(true);
        else setPollFailures((n) => n + 1);
      }
      setPollTick((n) => n + 1);
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [jobId, jobRunning, pollStopped, pollFailures, pollTick]);

  const pastedEmails = useMemo(() => parseEmailList(emailsText), [emailsText]);

  // The request as the form currently describes it, or why it can't be sent.
  const request = useMemo((): { body?: EmailSendRequest; problem?: string } => {
    const body: EmailSendRequest = {};
    if (source === 'template') {
      if (!templateId) return { problem: 'Pick a template.' };
      body.template = templateId;
      if (campaign.trim()) body.campaign = campaign.trim();
    } else {
      if (!campaign.trim()) return { problem: 'Custom copy needs a campaign id.' };
      body.campaign = campaign.trim();
      const copies: Partial<Record<EmailLang, CustomEmailCopy>> = {};
      for (const l of EMAIL_LANGS) {
        const c = content[l];
        const blank = !c.subject.trim() && !c.html.trim();
        if (l === 'ja' && blank && !c.text.trim() && !c.preheader.trim() && !c.cta.trim()) continue;
        if (!c.subject.trim() || !c.html.trim()) {
          return { problem: `${LANG_LABELS[l]} copy needs a subject and an HTML body.` };
        }
        copies[l] = {
          subject: c.subject,
          html: c.html,
          preheader: c.preheader,
          ...(c.text.trim() ? { text: c.text } : {}),
          ...(c.cta.trim() ? { cta: c.cta } : {}),
        };
      }
      body.content = copies;
      if (survey) body.survey = survey;
      const vars = parseVariables(variablesText);
      if (Object.keys(vars).length) body.variables = vars;
    }
    if (audience === 'list') {
      if (!pastedEmails.length) return { problem: 'Paste at least one email address.' };
      body.recipients = pastedEmails.map((email) => ({ email }));
    } else {
      body.segment = { type: segment, days };
    }
    return { body };
  }, [source, templateId, campaign, content, survey, variablesText, audience, pastedEmails, segment, days]);

  const requestKey = request.body ? JSON.stringify(request.body) : '';
  const dryRunIsCurrent = !!dryRun && dryRun.key === requestKey;

  const runDry = async () => {
    if (!request.body) return;
    setBusy('dry');
    setError('');
    setJob(null);
    setConfirming(false);
    try {
      setDryRun({ key: requestKey, result: await emailAdminApi.dryRun(request.body) });
    } catch (e) {
      setDryRun(null);
      setError(errorMessage(e, 'Dry run failed'));
    } finally {
      setBusy('');
    }
  };

  const sendForReal = async () => {
    if (!request.body || !dryRunIsCurrent) return;
    setBusy('send');
    setError('');
    try {
      const started = await emailAdminApi.send(request.body);
      const first = dryRun!.result.recipients[0];
      const preview = dryRun!.result.preview;
      setJob(preview && first ? { ...started, preview: { ...preview, email: first.email, lang: first.lang } } : started);
      setPollError('');
      setPollFailures(0);
      setPollStopped(false);
      setDryRun(null);
    } catch (e) {
      setError(errorMessage(e, 'Send failed'));
    } finally {
      setBusy('');
      setConfirming(false);
    }
  };

  const cancelJob = async () => {
    if (!job || !window.confirm('Stop this send? Emails already sent stay sent.')) return;
    setBusy('cancel');
    try {
      const cancelled = await emailAdminApi.cancelSendJob(job.jobId);
      setJob((current) => withPreview(cancelled, current));
    } catch (e) {
      setError(errorMessage(e, 'Cancel failed'));
    } finally {
      setBusy('');
    }
  };

  const willSend = dryRun?.result.summary.would_send || 0;
  const template = list.templates.find((t) => t.id === templateId);

  return (
    <div className="email-compose">
      <div className="admin-section">
        <h2>1. What to send</h2>
        <div className="admin-tabs" style={{ marginBottom: 12 }}>
          <button
            className={`admin-inline-btn ${source === 'template' ? 'active' : ''}`}
            onClick={() => setSource('template')}
          >
            Built-in template
          </button>
          <button
            className={`admin-inline-btn ${source === 'custom' ? 'active' : ''}`}
            onClick={() => setSource('custom')}
          >
            Custom copy
          </button>
        </div>

        {source === 'template' ? (
          <div className="email-fields">
            <label className="email-field">
              <span className="email-field-label">Template</span>
              <select
                className="admin-search"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {list.templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.id}
                    {t.customized ? ' (edited)' : ''}
                  </option>
                ))}
              </select>
              {template && (
                <span className="email-field-hint">
                  {template.description} Survey: {template.survey ?? 'none'}. Sent with its saved copy (edit it
                  under Templates).
                </span>
              )}
            </label>
            <label className="email-field">
              <span className="email-field-label">Campaign (optional)</span>
              <input
                className="admin-search"
                placeholder={templateId}
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
              />
              <span className="email-field-hint">
                Empty = <code>{templateId}</code>, which shares the dedupe log with the automatic
                email, so nobody who already got it gets it again. A new id (e.g.{' '}
                <code>{templateId}-resend-1</code>) emails everyone in the list once more.
              </span>
            </label>
          </div>
        ) : (
          <div className="email-fields">
            <label className="email-field">
              <span className="email-field-label">Campaign</span>
              <input
                className="admin-search"
                placeholder="e.g. 2026-11-hotels-launch"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
              />
              <span className="email-field-hint">
                Lowercase letters, digits, - and _. Each person gets a campaign at most once; survey
                answers are grouped by it.
              </span>
            </label>
            <label className="email-field">
              <span className="email-field-label">Survey</span>
              <select className="admin-search" value={survey} onChange={(e) => setSurvey(e.target.value)}>
                <option value="">None</option>
                {list.surveys.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <LangTabs
              lang={lang}
              onChange={setLang}
              marks={{ ja: content.ja.subject || content.ja.html ? '' : '(optional)' }}
            />
            {lang === 'ja' && (
              <span className="email-field-hint">
                Leave Japanese empty to send the English copy to everyone.
              </span>
            )}
            <CopyFields
              copy={content[lang]}
              onChange={(c) => setContent({ ...content, [lang]: c })}
              textHint="Optional. Generated from the HTML when empty."
            />
            <label className="email-field">
              <span className="email-field-label">Extra variables</span>
              <textarea
                className="admin-search email-code"
                rows={3}
                placeholder={'news = Callout can now call hotels\nnews_ja = ホテルにも電話できるようになりました'}
                value={variablesText}
                onChange={(e) => setVariablesText(e.target.value)}
              />
              <span className="email-field-hint">
                One <code>key = value</code> per line, used as {'{{key}}'}. Built in:{' '}
                {list.variables.map((v) => `{{${v}}}`).join(' ')}
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h2>2. Who gets it</h2>
        <div className="admin-tabs" style={{ marginBottom: 12 }}>
          <button
            className={`admin-inline-btn ${audience === 'list' ? 'active' : ''}`}
            onClick={() => setAudience('list')}
          >
            Email list
          </button>
          <button
            className={`admin-inline-btn ${audience === 'segment' ? 'active' : ''}`}
            onClick={() => setAudience('segment')}
          >
            Segment
          </button>
        </div>
        {audience === 'list' ? (
          <label className="email-field">
            <textarea
              className="admin-search email-code"
              rows={5}
              placeholder="one@example.com, two@example.com …"
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
            />
            <span className="email-field-hint">
              {pastedEmails.length} address{pastedEmails.length === 1 ? '' : 'es'}. Addresses that
              match an account get its name and language. Up to 500 per send.
            </span>
          </label>
        ) : (
          <div className="admin-tabs" style={{ alignItems: 'center' }}>
            <select
              className="admin-search"
              value={segment}
              onChange={(e) => setSegment(e.target.value as EmailSegmentType)}
            >
              {(list.segments as EmailSegmentType[]).map((s) => (
                <option key={s} value={s}>
                  {SEGMENT_LABELS[s] || s}
                </option>
              ))}
            </select>
            <input
              className="admin-search"
              style={{ minWidth: 0, width: 90 }}
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
            />
            <span className="admin-muted">days</span>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h2>3. Dry run, then send</h2>
        {request.problem && <div className="admin-muted">{request.problem}</div>}
        <div className="admin-tabs">
          <button
            className="admin-inline-btn"
            disabled={!request.body || busy !== ''}
            onClick={runDry}
          >
            {busy === 'dry' ? 'Checking…' : dryRun ? 'Run the dry run again' : 'Dry run'}
          </button>
          {dryRunIsCurrent && !confirming && (
            <button
              className="admin-primary-btn"
              disabled={willSend === 0 || busy !== '' || !dryRun!.result.configured}
              onClick={() => setConfirming(true)}
            >
              Send to {willSend} recipient{willSend === 1 ? '' : 's'}…
            </button>
          )}
        </div>
        {dryRun && !dryRunIsCurrent && (
          <div className="admin-notice">The form changed since the dry run. Run it again before sending.</div>
        )}
        {error && <div className="admin-error">{error}</div>}

        {confirming && dryRunIsCurrent && (
          <div className="email-confirm">
            <strong>
              Send {willSend} email{willSend === 1 ? '' : 's'} for campaign “{dryRun!.result.campaign}”
              now?
            </strong>
            <div className="admin-muted" style={{ margin: '6px 0 12px' }}>
              This sends real email and can't be undone. Already-emailed and unsubscribed recipients
              are skipped. Sending is paced at about 1.5 s per email
              {willSend > 20 ? ` (~${Math.ceil((willSend * 1.5) / 60)} min)` : ''} and runs on the
              server, so you can leave this page and come back to check on it.
            </div>
            <div className="admin-tabs">
              <button className="admin-danger-btn" disabled={busy !== ''} onClick={sendForReal}>
                {busy === 'send' ? 'Starting…' : `Yes, send ${willSend}`}
              </button>
              <button
                className="admin-inline-btn"
                disabled={busy !== ''}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {job && (
          <SendJobPanel
            job={job}
            pollError={pollError}
            pollStopped={pollStopped}
            cancelling={busy === 'cancel'}
            onCancel={cancelJob}
            onClose={() => setJob(null)}
          />
        )}

        {dryRun && (
          <div className="email-editor-grid" style={{ marginTop: 16 }}>
            <div>
              {!dryRun.result.configured && (
                <div className="admin-error">
                  Sending isn't configured on this backend (SMTP / EMAIL_TOKEN_SECRET), so a real
                  send would fail. The dry run still works.
                </div>
              )}
              <div className="admin-tabs" style={{ marginBottom: 10 }}>
                <span className="admin-muted">Campaign “{dryRun.result.campaign}”:</span>
                {Object.entries(dryRun.result.summary).map(([k, v]) => (
                  <span key={k}>
                    <EmailStatusBadge status={k} /> {v}
                  </span>
                ))}
                {dryRun.result.recipients.length === 0 && <span className="admin-muted">no recipients</span>}
              </div>
              <RecipientTable rows={dryRun.result.recipients} />
            </div>
            <EmailPreview
              email={dryRun.result.preview}
              caption={
                dryRun.result.recipients[0]
                  ? `Preview for ${dryRun.result.recipients[0].email} (${dryRun.result.recipients[0].lang})`
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

const JOB_STATUS_TEXT: Record<EmailSendJob['status'], string> = {
  running: 'Sending',
  completed: 'Done',
  cancelled: 'Cancelled',
  failed: 'Stopped',
};

/** Progress and results of a real send, updated while it runs. */
function SendJobPanel({
  job,
  pollError,
  pollStopped,
  cancelling,
  onCancel,
  onClose,
}: {
  job: EmailSendJob;
  pollError: string;
  pollStopped: boolean;
  cancelling: boolean;
  onCancel: () => void;
  onClose: () => void;
}) {
  const running = job.status === 'running';
  const summary = Object.entries(job.summary)
    .map(([k, v]) => `${v} ${k}`)
    .join(', ');
  return (
    <div className="email-send-job" style={{ marginTop: 16 }}>
      <div className="admin-tabs" style={{ alignItems: 'center' }}>
        <strong>
          {JOB_STATUS_TEXT[job.status] || job.status} · campaign “{job.campaign}” · {job.processed}{' '}
          of {job.total}
        </strong>
        {running ? (
          <button className="admin-inline-btn" disabled={cancelling} onClick={onCancel}>
            {cancelling ? 'Stopping…' : 'Stop sending'}
          </button>
        ) : (
          <button className="admin-inline-btn" onClick={onClose}>
            Close
          </button>
        )}
      </div>
      <progress
        className="email-send-progress"
        max={Math.max(job.total, 1)}
        value={job.processed}
        aria-label="Send progress"
      />
      <div className="admin-notice">
        {summary || (running ? 'Starting…' : 'Nobody was emailed.')}
        {running &&
          ' · Sends in batches while this page is open, and every minute on the server once the scheduler tick is set up (see EMAIL.md).'}
        {!running &&
          job.status !== 'completed' &&
          job.processed < job.total &&
          ` · ${job.total - job.processed} not attempted. Sending the same request again reaches only people who weren't emailed yet.`}
      </div>
      {job.stalled && (
        <div className="admin-notice">
          No progress for a few minutes. Keep this page open to continue it, or check that the
          scheduler tick is running.
        </div>
      )}
      {job.error && <div className="admin-error">{job.error}</div>}
      {pollError && (
        <div className="admin-error">
          {pollStopped ? `Stopped checking progress: ${pollError}` : `${pollError} (retrying)`}
        </div>
      )}
      <div className="email-editor-grid" style={{ marginTop: 12 }}>
        <div>
          <RecipientTable rows={job.results || []} />
        </div>
        {job.preview !== undefined && (
          <EmailPreview
            email={job.preview}
            caption={job.preview ? `As sent to ${job.preview.email} (${job.preview.lang})` : undefined}
          />
        )}
      </div>
    </div>
  );
}

/** Progress updates don't carry the rendered email; keep the one already shown. */
function withPreview(next: EmailSendJob, current: EmailSendJob | null): EmailSendJob {
  return next.preview === undefined && current?.jobId === next.jobId
    ? { ...next, preview: current.preview }
    : next;
}

function RecipientTable({
  rows,
}: {
  rows: { email: string; uid?: string | null; lang?: string; status: string; error?: string }[];
}) {
  if (!rows.length) return null;
  return (
    <div className="admin-table-wrap email-recipients">
      <table className="admin-table static">
        <thead>
          <tr>
            <th>Email</th>
            <th>Lang</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.email}>
              <td>
                {r.uid ? <Link to={`/admin/users/${r.uid}`}>{r.email}</Link> : r.email}
              </td>
              <td>{r.lang || '—'}</td>
              <td>
                <EmailStatusBadge status={r.status} />
                {r.error && <span className="admin-muted"> {r.error}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

function AdminEmails() {
  const [params, setParams] = useSearchParams();
  const section = (params.get('section') as Section) || 'activity';
  const openTemplate = params.get('template');
  const [list, setList] = useState<EmailTemplateList | null>(null);
  const [error, setError] = useState('');

  const update = useCallback(
    (changes: Record<string, string | null>) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v === null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      }),
    [setParams]
  );

  const loadList = useCallback(() => {
    emailAdminApi
      .templates()
      .then(setList)
      .catch((e) => setError(errorMessage(e, 'Failed to load templates')));
  }, []);

  useEffect(loadList, [loadList]);

  const sections: [Section, string][] = [
    ['activity', 'Recent activity'],
    ['templates', 'Templates'],
    ['compose', 'Compose & send'],
  ];

  return (
    <div className="admin-section" style={{ marginTop: 0 }}>
      <div className="admin-tabs" style={{ marginBottom: 16 }}>
        {sections.map(([key, label]) => (
          <button
            key={key}
            className={`admin-inline-btn ${section === key ? 'active' : ''}`}
            onClick={() => update({ section: key, template: null, job: null })}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="admin-error">{error}</div>}

      {section === 'activity' && <AdminActivity />}

      {section === 'templates' &&
        (openTemplate ? (
          <TemplateEditor
            templateId={openTemplate}
            onBack={() => {
              update({ template: null });
              loadList();
            }}
          />
        ) : list ? (
          <>
            <p className="admin-muted" style={{ marginTop: 0 }}>
              These go out automatically. Edits apply to the next email sent; reset returns a
              template to the built-in copy.
            </p>
            <TemplateList list={list} onOpen={(id) => update({ template: id })} />
          </>
        ) : (
          !error && <div className="admin-muted">Loading…</div>
        ))}

      {section === 'compose' &&
        (list ? (
          <Compose list={list} initialTo={params.get('to') || ''} initialJob={params.get('job') || ''} />
        ) : (
          !error && <div className="admin-muted">Loading…</div>
        ))}
    </div>
  );
}

export default AdminEmails;
